import { debounce } from '@/packages/debounce';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

type MutableRefObject<T> = {
    current: T;
};

export const useEffectInEvent = <K extends keyof WindowEventMap>(event: K, set: () => void, useCapture?: boolean) => {
    useEffect(() => {
        if (set) {
            set();
            window.addEventListener(event, set, useCapture);

            return () => window.removeEventListener(event, set, useCapture);
        }
    }, [event, set, useCapture]);
};

export default function useBoundingBox<T extends HTMLElement | null>(
    debounceTime = 0,
): [DOMRect | undefined, MutableRefObject<T | null>] {
    const ref = useRef<T>(null);
    const [rect, setRect] = useState<DOMRect>();

    const set = useCallback((): void => {
        setRect(ref.current?.getBoundingClientRect());
    }, []);

    // eslint-disable-next-line react-hooks/refs
    const debounceSet = useMemo(() => debounce(set, debounceTime), [set, debounceTime]);

    useEffectInEvent('resize', debounceSet);
    useEffectInEvent('scroll', debounceSet, true);

    return [rect, ref];
}
